import { useState } from 'react';

const SECTIONS = [
  {
    title: 'How to add a new client',
    content: [
      { step: 1, text: 'Go to Organizations and click "New Organization". Fill in the name, short name, type, and country.' },
      { step: 2, text: 'Go to Users and click "New User". Select the organization, enter their email, name, and assign the role "client_manager".' },
      { step: 3, text: 'Go to Products and click "New Product Line". Select the organization and enter the product line name (e.g., RISET) and full name.' },
      { step: 4, text: 'Open the product line and click "Add AIBOX" to register appliances. Each AIBOX gets 3 auto-generated license keys.' },
    ],
  },
  {
    title: 'How to register appliances',
    content: [
      { step: 0, heading: 'Method 1: Manual (Add AIBOX)', text: 'Open Products, select a product line, click "Add AIBOX". Enter the unit name and serial number (or leave blank to auto-generate). Three license keys are created automatically: AI Core License, AI Agentic License, and the platform-specific license.' },
      { step: 0, heading: 'Method 2: Bulk (Import Excel)', text: 'Go to Import Excel, download the template, fill in your data (one sheet per product line, columns: NO, PRODUCT NAME, License, LICENSE KEY), then upload and confirm. All appliances and licenses are created in one batch.' },
    ],
  },
  {
    title: 'How to generate certificates',
    content: [
      { step: 1, text: 'Go to Products and click on a product line card to open its detail page.' },
      { step: 2, text: 'Click the "Generate Certificate" button at the top. This generates a multi-page PDF for each AIBOX (3 pages per unit, one per license).' },
      { step: 3, text: 'If certificates already exist, you will be asked to confirm regeneration.' },
      { step: 4, text: 'Download the generated PDFs from the Certificates page. Each PDF contains the TRDATA logo, license details, owner name, and license key.' },
    ],
  },
  {
    title: 'How to activate licenses (Admin)',
    content: [
      { step: 1, text: 'Go to Products and click on a product line to see all AIBOX units.' },
      { step: 2, text: 'Find the AIBOX that needs activation. It will show a "Pending" status pill with an "Activate" button next to it.' },
      { step: 3, text: 'Click "Activate" and confirm. All 3 licenses for that AIBOX will be activated simultaneously.' },
      { step: 4, text: 'The status changes to "Activated" and the license pills turn green.' },
    ],
  },
  {
    title: 'How to activate licenses (Client)',
    content: [
      { step: 1, text: 'Client logs in to the portal at /central and navigates to "Activate Appliance".' },
      { step: 2, text: 'Client enters any ONE of their 3 license keys from the License Key Authorization card.' },
      { step: 3, text: 'The system shows the AIBOX details and ALL 3 licenses that will be activated.' },
      { step: 4, text: 'Client clicks "Activate All 3 Licenses". All licenses become active and the appliance status changes to Activated.' },
    ],
  },
  {
    title: 'Excel import format',
    content: [
      { step: 0, heading: 'File Structure', text: 'Each sheet in the Excel file represents one product line. The sheet name becomes the product line name (e.g., "RISET", "PENT TEST").' },
      { step: 0, heading: 'Required Columns', text: 'NO | PRODUCT NAME | License | LICENSE KEY' },
      { step: 0, heading: 'Row Layout', text: 'Each appliance uses 3 rows (one per license). The first row has the NO and PRODUCT NAME, the next two rows leave those columns empty. Each row has the License name and LICENSE KEY.' },
      { step: 0, heading: 'Example', text: '1 | AI BOX | AI Core License | Z4TN-8QKP-L7MA-2RDX\n  |        | AI Agentic License | R6QM-2ZPK-8LTF-4XNA\n  |        | Riset Platform License | F4ZM-8KLP-2QTR-9XHA' },
    ],
  },
  {
    title: 'License key format',
    content: [
      { step: 0, heading: 'Format', text: 'XXXX-XXXX-XXXX-XXXX — 16 alphanumeric characters separated by dashes into 4 groups of 4.' },
      { step: 0, heading: 'Character Set', text: 'Uses uppercase letters and digits, excluding commonly confused characters: 0 (zero) and O (letter), 1 (one) and I (letter), 5 and S are excluded.' },
      { step: 0, heading: 'Uniqueness', text: 'Each license key is globally unique across both the License and Certificate tables. Keys are auto-generated when registering appliances.' },
    ],
  },
];

function ChevronIcon({ open }) {
  return (
    <svg
      className={`w-[18px] h-[18px] text-[#475569] transition-transform duration-200 flex-shrink-0 ${open ? 'rotate-180' : ''}`}
      viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"
    >
      <polyline points="6 9 12 15 18 9"/>
    </svg>
  );
}

export default function Help() {
  const [openSections, setOpenSections] = useState({ 0: true });

  const toggle = (i) => {
    setOpenSections(prev => ({ ...prev, [i]: !prev[i] }));
  };

  return (
    <div>
      <div className="mb-6">
        <div className="text-[11px] text-[#475569] mb-[2px]">Admin</div>
        <h2 className="text-[22px] font-bold text-[#e2e8f0] tracking-[-0.3px]">Getting Started</h2>
        <p className="text-[13px] text-[#64748b] mt-[2px]">Step-by-step guides for managing the Tritech Central platform</p>
      </div>

      <div className="flex flex-col gap-[6px]">
        {SECTIONS.map((section, i) => {
          const isOpen = !!openSections[i];
          return (
            <div key={i} className="bg-[#0f1623] border border-[rgba(148,163,184,0.06)] rounded-[10px] overflow-hidden">
              <button
                onClick={() => toggle(i)}
                className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-[rgba(148,163,184,0.03)] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="w-7 h-7 rounded-lg bg-[rgba(6,182,212,0.1)] text-[#06b6d4] text-[12px] font-bold flex items-center justify-center flex-shrink-0">
                    {i + 1}
                  </span>
                  <span className="text-[14px] font-semibold text-[#e2e8f0]">{section.title}</span>
                </div>
                <ChevronIcon open={isOpen} />
              </button>

              {isOpen && (
                <div className="border-t border-[rgba(148,163,184,0.06)] px-5 py-4">
                  <div className="space-y-3">
                    {section.content.map((item, j) => (
                      <div key={j} className="flex gap-3">
                        {item.step > 0 && (
                          <span className="w-5 h-5 rounded-full bg-[rgba(148,163,184,0.06)] text-[#64748b] text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                            {item.step}
                          </span>
                        )}
                        <div className={item.step === 0 ? 'w-full' : ''}>
                          {item.heading && (
                            <p className="text-[12px] font-semibold text-[#94a3b8] mb-1">{item.heading}</p>
                          )}
                          <p className="text-[12.5px] text-[#64748b] leading-[1.6] whitespace-pre-line">{item.text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
